import Link from 'next/link'
import React from 'react'

import { DateTime } from 'luxon'

import { Box, Divider, Grid } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import ExtendedButton from '~/components/shared/ExtendedButton'
import Text from '~/components/shared/Text'

import Paths from '~/utils/paths'

import Book from '~/assets/icons/book.svg?sprite'
import Calendar from '~/assets/icons/calendar.svg?sprite'
import Camera from '~/assets/icons/camera.svg?sprite'
import { Theme } from '~/theme/theme'

import { BlogArticleExternalLink } from '~/models/Blog'

import BlogExternalLink from './BlogExternalLink'

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      container: {
        borderRadius: '4px',
        boxShadow: '0px 7px 10px rgba(0, 0, 0, 0.08)'
      },
      tagsBtn: {
        minHeight: '18px',
        marginRight: '4px',
        marginTop: '5px',
        padding: '2px 5px',
        '& p': {
          color: theme.palette.primary.contrastText
        }
      }
    }),
  { name: 'BlogArticleCardInfo' }
)

type Props = {
  tags?: string[]
  author: string
  date?: string
  photographer?: string
  externalLinks?: BlogArticleExternalLink[]
}

const BlogArticleCardInfo = ({
  tags = [],
  author,
  date,
  photographer,
  externalLinks = []
}: Props): JSX.Element => {
  const classes = useStyles()

  return (
    <Box p={2} className={classes.container}>
      <Box>
        <Text fontWeight={600} fontSize={20} mb={2}>
          Tags
        </Text>
      </Box>
      {!!tags.length && (
        <Box mb={2}>
          {tags.map((tag) => {
            return (
              <Link
                key={tag}
                href={Paths.newsReviews.buildSearch({ keywords: tag })}
                passHref
              >
                <ExtendedButton className={classes.tagsBtn}>
                  <Text
                    fontSize={10}
                    textTransform="uppercase"
                    letterSpacing={1}
                  >
                    {tag}
                  </Text>
                </ExtendedButton>
              </Link>
            )
          })}
        </Box>
      )}
      <Divider variant="middle" />
      <Grid container spacing={2} style={{ marginTop: '15px' }}>
        <Grid item>
          {author && (
            <Box mt={1}>
              <Book />
            </Box>
          )}
          {date && (
            <Box mt={4}>
              <Calendar />
            </Box>
          )}
          {photographer && (
            <Box mt={4}>
              <Camera />
            </Box>
          )}
        </Grid>
        <Grid item style={{ maxWidth: '240px' }}>
          {author && (
            <Box mb={2}>
              <Text color="primary" fontSize={12}>
                Author
              </Text>
              <Text>{author}</Text>
            </Box>
          )}
          {date && (
            <Box mb={2}>
              <Text color="primary" fontSize={12}>
                Date
              </Text>
              <Text>{DateTime.fromISO(date).toFormat('d MMM y')}</Text>
            </Box>
          )}
          {photographer && (
            <Box mb={2}>
              <Text color="primary" fontSize={12}>
                Photographer
              </Text>
              <Text>{photographer}</Text>
            </Box>
          )}
        </Grid>
      </Grid>
      {!!externalLinks.length && (
        <>
          <Divider variant="middle" />
          <Box mt={2} alignItems="center" display="flex" flexWrap="wrap">
            {externalLinks.map((link) => {
              return (
                <BlogExternalLink
                  key={link.external_link_url}
                  href={link.external_link_url}
                  title={link.link_description}
                  style={{ marginRight: 8, overflowWrap: 'anywhere' }}
                />
              )
            })}
          </Box>
        </>
      )}
    </Box>
  )
}

export default BlogArticleCardInfo
